<template>
  <!-- eslint-disable max-len -->
  <div class="modal-parent">
    <div
      id="add-user-modal"
      v-shortkey="['esc']"
      class="modal"
      @shortkey="closeModal"
    >
      <div class="modal_topbar modal_topbar--no-actions">
        <div
          id="title-user-modal"
          class="modal_topbar-title modal_topbar-title--no-actions"
          style="display: inline-flex"
        >
          <span>
            {{ isEditMode ? `Edit ${name}` : "Add User" }}
          </span>
          <user-icon :roles="roles" />
        </div>
        <div class="modal_topbar_action modal_topbar_action--no-actions">
          <template v-if="loading">
            <loader :size="25" />
          </template>
        </div>
        <span
          id="exit-user-modal"
          class="modal_topbar-exit"
          @click="closeModal"
          v-html="closeIcon"
        />
      </div>
      <div class="flex-grid">
        <template v-for="field in userFields">
          <div
            :key="field.id"
            class="form-container column--two"
          >
            <div class="form-label-container">
              <div class="info-icon-container">
                <label class="form-label">{{ field.label }}</label>
                <template v-if="field.infoMessage">
                  <span
                    v-tooltip="{ content: field.infoMessage, trigger: 'mouseenter click focus' }"
                    class="info-icon"
                    v-html="infoIcon"
                  />
                </template>
              </div>
              <div 
                :id="field.id + '-input-error'"
                class="form__error">
                {{ inputErrors[field.id] }}
              </div>
            </div>
            <template v-if="field.id === 'phoneNumber'">
              <vue-tel-input
                id="phoneNumber-user-modal"
                v-model="field.input"
                :mode="'international'"
                :input-classes="'form-field'"
                :wrapper-classes="'phone-wrapper-field'"
                :disabled-fetching-country="true"
                :default-country="'CA'"
                :preferred-countries="['CA', 'US', 'GB']"
                :placeholder="''"
                @validate="handlePhoneValidation"
              />
            </template>
            <template v-else>
              <input
                :id="field.id + '-user-modal'"
                v-model.lazy="field.input"
                class="form-field"
                type="text"
                :disabled="field.disabled"
              />
            </template>
          </div>
        </template>
      </div>
      <div class="flex-grid">
        <!-- override styling to align Admin role options to the top of the grid to not move when admin password component is rendered -->
        <div
          class="form-container column--two"
          style="display: inline"
        >
          <div class="form-label-container mb-1">
            <label class="form-label">{{ admin.label }}</label>
          </div>
          <label
            class="radio role-option pb-1"
            for="user-role"
          >
            <input
              id="user-role"
              v-model="admin.role"
              type="radio"
              value="user"
              :disabled="isCurrentUser"
            />
            <span> User </span>
            <span class="role-tooltip ml-1"> No online access </span>
          </label>
          <label
            class="radio role-option pb-1"
            for="group-admin-role"
          >
            <input
              id="group-admin-role"
              v-model="admin.role"
              type="radio"
              value="groupAdmin"
              :disabled="isCurrentUser"
            />
            <span> Group Administrator </span>
            <span class="role-tooltip ml-1">
              Online access with limited permissions to manage groups
            </span>
          </label>
          <label
            v-tooltip="{ content: accountAdminRoleTooltipMessage }"
            class="radio role-option pb-1"
            for="account-admin-role"
          >
            <input
              id="account-admin-role"
              v-model="admin.role"
              type="radio"
              value="administrator"
              :disabled="isCurrentUser || !canAssignAccountAdmin"
            />
            <span> Account Administrator </span>
            <span class="role-tooltip ml-1">
              Online access with full permissions on the account
            </span>
          </label>
        </div>
        <template v-if="canShowPaswordField">
          <div class="form-container column--two">
            <div class="form-label-container">
              <label class="form-label">Set Password</label>
              <div class="form__error">
                {{ passwordValidationMessage }}
              </div>
            </div>
            <password-field :password.sync="admin.password" />
            <password-validation
              :password="admin.password"
              :is-valid.sync="passwordValidation"
            />
          </div>
        </template>
        <template v-if="isNewAdmin">
          <div
            class="alert alert-action"
            data-testid="password-reset-info-div"
          >
            <strong>Action Required </strong>
            <p class="m-0">
              {{ user.firstName }} is being assigned a role that has online access to the web
              console. To login, the user will need to navigate to
              <a
                :href="loginUrl"
                target="_blank"
              >link.bioconnect.cloud</a> and select
              <strong> Forgot Password </strong> to set their password.
            </p>
          </div>
        </template>
      </div>
      <div class="modal_footer">
        <template v-if="isEditMode">
          <div v-tooltip="{ content: deleteTooltipMessage }">
            <button
              v-show="!isCurrentUser"
              id="delete-button-user-modal"
              class="btn btn__text btn__text--red"
              type="submit"
              :disabled="!canDeleteUser || loading"
              @click="emitDelete"
            >
              Delete User
            </button>
          </div>
        </template>
        <template v-else>
          <label
            class="checkbox"
            for="add-card"
          >
            <input
              id="add-card"
              v-model="addCard"
              type="checkbox"
            />
            <span>Add Credential</span>
          </label>
        </template>
        <div class="error">
          {{ errorMessage }}
        </div>
        <button
          id="add-button-user-modal"
          class="btn"
          type="submit"
          :disabled="hasErrors || loading"
          @click="emitSubmit"
        >
          {{ isEditMode ? "Update User" : "Add User" }}
        </button>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import constants from '@/global/constants.js'
import Loader from './loader'
import feather from 'feather-icons'
import REGEX from '@/global/utilities.js'
import {VueTelInput} from 'vue-tel-input'
import PasswordField from '@/components/passwordField.vue'
import PasswordValidation from '@/components/passwordValidation.vue'
import UserIcon from './userIcon'

export default {
  name: 'UserModal',
  components: {
    loader: Loader,
    VueTelInput,
    PasswordField,
    PasswordValidation,
    UserIcon,
  },
  props: ['loading', 'error', 'mode', 'user'],
  data() {
    return {
      passwordValidation: false,
      addCard: false,
      errorMessage: '',
      isPhoneValid: false,
      loginUrl: '',
      userFields: [],
      defaultField: {
        input: '',
        required: false,
        disabled: false,
        validation: null,
        errorMessage: null,
        infoMessage: null,
      },
      admin: {
        role: '',
        password: '',
        validation: REGEX.password,
        label: 'Role',
      },
      canDeleteUser: false,
      canAssignAccountAdmin: false,
      closeIcon: feather.icons.x.toSvg(),
      infoIcon: feather.icons.info.toSvg({width: '1em', height: '1em'}),
      adminIcon: feather.icons.shield.toSvg({width: '1em', height: '1em'}),
      groupAdminIcon: feather.icons.users.toSvg({width: '1em', height: '1em'}),
    }
  },
  computed: {
    hasErrors() {
      return (
        Object.keys(this.inputErrors).length > 0
        || (!this.passwordValidation && this.admin.role !== 'user' && !this.isEditMode)
      )
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    acmUser() {
      return (
        this.isEditMode
        && (this.user.email !== this.user.sanitizedEmail
          || this.user.username !== this.user.sanitizedUsername)
      )
    },
    name() {
      if (this.user) {
        return `${this.user.firstName} ${this.user.lastName}`
      }
      return 'User'
    },
    roles() {
      if (this.user) {
        return this.user.roles
      }
      return null
    },
    inputErrors() {
      return this.userFields.reduce((errors, field) => {
        const {input, validation, id, required, errorMessage} = field
        if (input) {
          if (this.isInputValid(field)) {
            errors[id] = errorMessage
          }
        } else if (required) {
          errors[id] = 'Required'
        }
        return errors
      }, {})
    },
    passwordValidationMessage() {
      const {password, validation, role} = this.admin
      // Perform password validation for admin roles only
      if (!this.isEditMode && role !== 'user') {
        if (password) {
          return this.passwordValidation ? null : 'Invalid Password'
        }
        return 'Required'
      }
      return null
    },
    canShowPaswordField() {
      return !this.isEditMode && this.admin.role !== 'user'
    },
    isNewAdmin() {
      return this.user && this.user.roles[0].name === 'user' && this.admin.role !== 'user'
    },
    isCurrentUser() {
      const _currentUser = this.getUsername()
      return this.user ? this.user.username === _currentUser : false
    },
    deleteTooltipMessage() {
      return this.canDeleteUser ? '' : constants.actionNotAvailableMessage
    },
    accountAdminRoleTooltipMessage() {
      return this.canAssignAccountAdmin ? '' : constants.actionNotAvailableMessage
    },
  },
  watch: {
    admin: {
      deep: true,
      handler() {
        if (this.admin.role === 'user') {
          this.admin.password = ''
        }
      },
    },
  },
  created() {
    this.canDeleteUser = this.$can('delete', 'user')
    this.canAssignAccountAdmin = this.$can('update', 'role-admin')
    this.initUserFields()

    // Since a user can only be 1 of 3 roles: user, admin or groupAdmin
    if (this.isAdmin()) {
      this.admin.role = 'administrator'
    } else if (this.isGroupAdmin()) {
      this.admin.role = 'groupAdmin'
    } else {
      this.admin.role = 'user'
    }

    this.loginUrl = this.$router.resolve({name: 'login'}).href
  },
  methods: {
    initUserFields() {
      this.userFields = 
      [
        this.createUserField({
          id: 'firstName',
          label: 'First Name',
          validation: REGEX.name,
          errorMessage: 'Please enter a valid name.',
          required: true,
        }),
        this.createUserField({
          id: 'lastName',
          label: 'Last Name',
          validation: REGEX.name,
          errorMessage: 'Please enter a valid name.',
          required: true,
        }),
        this.createUserField({
          id: this.acmUser ? 'sanitizedEmail' : 'email',
          label: 'Email',
          errorMessage: 'Please enter a valid email.',
          validation: REGEX.email,
          required: true,
          disabled: this.acmUser,
        }),
        this.createUserField({
          id: 'phoneNumber',
          label: 'Phone Number',
          errorMessage: 'Please enter a valid phone number.',
          infoMessage: 'Must be filled in for SMS authentication',
        }),
        this.createUserField({
          id: this.acmUser ? 'sanitizedUsername' : 'username',
          label: 'Authenticator Username',
          required: true,
          infoMessage: 'Username from your second-factor authentication application',
          disabled: this.isEditMode || this.acmUser,
        }),
      ]

      if (this.user) {
        this.userFields = this.userFields.map(field => {
          return {
            ...field,
            input: this.user[field.id] ? this.user[field.id] : '',
          }
        })
      }
    },
    createUserField(overrides) {
      return { ...this.defaultField, ...overrides }
    },
    // Used for inputErrors computed property
    isInputValid(field) {
      const {input, validation, id, required, errorMessage} = field
      return (
        (id === 'phoneNumber' && !this.isPhoneValid) || (validation && !validation.test(input))
      )
    },
    handlePhoneValidation(phoneObject) {
      this.isPhoneValid = phoneObject.isValid
    },
    toggleAdminRadioButton() {
      this.admin.role = !this.admin.role
    },
    closeModal() {
      this.$emit('close')
      this.clearInput()
    },
    clearInput() {
      this.userFields.forEach((field) => {
        field.input = ''
      })
    },
    isAdmin() {
      if (this.user && this.user.roles && this.user.roles.length > 0) {
        return this.user.roles[0].name === 'administrator'
      }
    },
    isGroupAdmin() {
      if (this.user && this.user.roles && this.user.roles.length > 0) {
        return this.user.roles[0].name === 'groupAdmin'
      }
    },
    emitDelete() {
      if (this.canDeleteUser) {
        this.$emit('delete', this.user.username)
      }
    },
    emitSubmit() {
      this.errorMessage = ''
      const _userPassword = 'Stay71(oldie'
      if (this.hasErrors) {
        this.errorMessage = 'Please make sure all fields are filled in correctly.'
      } else {
        const _user = {
          addCard: this.addCard,
          role: this.isCurrentUser ? null : this.admin.role,
          password: this.admin.password ? this.admin.password : _userPassword,
          bio: '',
        }
        this.userFields.forEach((field) => {
          if (field.id === 'phoneNumber') {
            _user[field.id] = field.input.replace(/[ -]/gm, '')
          } else {
            _user[field.id] = field.input
          }
        })
        this.$emit('submit', _user)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.role-option {
  @include flex-display();
  @include flex-justify-content(space-between);
}

.role-tooltip {
  text-align: right;

  color: $grey-text;
  font-weight: lighter;
  font-size: $small-font;

  // if element shrinks, wrap the text to the next line
  overflow-wrap: break-word;
  white-space: normal;

  // when the text overflows, make sure it's still right-aligned
  &::after {
    content: "";
    display: inline-block;
    width: 100%;
    height: 0;
  }
}
</style>
